package com.islamicmedia.shared.domain.model

sealed class AnalysisProgress {
    data class Disambiguating(val query: String) : AnalysisProgress()
    data class DisambiguationComplete(val items: List<DisambiguationItem>, val journeyId: String? = null) : AnalysisProgress()
    data class Searching(val stepNumber: Int, val query: String) : AnalysisProgress()
    data class SearchComplete(val stepNumber: Int, val durationMs: Long, val costUSD: Double) : AnalysisProgress()
    data object Summarizing : AnalysisProgress()
    data class ContentCacheHit(val title: String) : AnalysisProgress()
    data class AnalysisCacheHit(val title: String) : AnalysisProgress()
    data class Analyzing(val title: String, val totalConcerns: Int) : AnalysisProgress()
    data class ConcernComplete(val concernIndex: Int, val totalConcerns: Int, val concernName: String) : AnalysisProgress()
    /**
     * The server's 15s keep-alive. Carries no progress — it exists purely as a LIVENESS signal so
     * the client can tell "Phase 2 is grinding away" apart from "the socket died and no one noticed".
     * Without it the only failure signal is the 600s socket timeout, which strands a task for ten
     * minutes; see the watchdog in MediaCheckerViewModel.
     */
    data object Heartbeat : AnalysisProgress()

    data object CrossReferencing : AnalysisProgress()
    data object CompilingReport : AnalysisProgress()
    data object EnrichingWithGuidance : AnalysisProgress()

    /**
     * V2: Phase 1 verdict ready. Contains the partial AnalysisResult (severities, score,
     * islamicScore, positiveAspects). UI can render the verdict immediately.
     *
     * In Quick mode this is the final result — AnalysisComplete follows shortly with the
     * same data and quickCheck=true.
     *
     * In Full mode this fires before Phase 2 enrichment runs. The UI shows the verdict early;
     * Phase 2 enrichments arrive via subsequent ConcernEnriched events, and the final
     * AnalysisComplete carries the fully-enriched result.
     */
    data class Phase1Complete(val partialResult: AnalysisResult) : AnalysisProgress()

    /**
     * V2: Phase 2 has started and reported its true unit count. [total] is the full unit set
     * (flagged concerns + custom concerns + verdict-meta + developmental); [alreadyDone] is how many
     * were already cached from an earlier partial run and so will never emit a [ConcernEnriched].
     * Only the server can know either number — see the emit in runPhase2.
     */
    data class Phase2Started(val total: Int, val alreadyDone: Int) : AnalysisProgress()

    /**
     * V2: a single Phase 2 enrichment unit completed. Name only (big SSE frames are unreliable on
     * iOS); the ViewModel fetches the finished write-up itself so the card can fill in right away.
     * [unit] uses the server's unit names: "concern:<category>", "custom:<n>", "verdict_meta",
     * "developmental".
     */
    data class ConcernEnriched(val unit: String) : AnalysisProgress()

    /** The TL;DR bullets, streamed right after the verdict instead of arriving with the result. */
    data class TldrReady(val points: List<TldrPoint>) : AnalysisProgress()

    /** V2: Phase 2 finished but some units failed. UI shows retry icons for the listed unit names. */
    data class PartialComplete(val failedUnits: List<String>) : AnalysisProgress()

    data class AnalysisComplete(val result: AnalysisResult, val costUSD: Double) : AnalysisProgress()
    data class Error(val message: String) : AnalysisProgress()
    /**
     * The server refused to start this analysis because the account is at its fair-use daily
     * limit (the `daily_cap` error frame on /api/analyze). Deliberately NOT an [Error]: every
     * recovery path (auto-retry, /api/resume, the paywall and sign-in backstops, the error report)
     * hangs off [Error], and none of them may fire for this. See 2026-09-22_daily_analysis_cap.sql.
     */
    data class DailyLimitReached(val notice: DailyLimitNotice) : AnalysisProgress()
    data object Cancelled : AnalysisProgress()
}
